// EmotionalRhythmTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Waves } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import { UKV2TextInput, UKV2SubmitButton } from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2EmotionalRhythmData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Emotional Rhythm";

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2EmotionalRhythmData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Waves className="w-5 h-5" />}
          title={t("ukv2.tabs.emotionalRhythm.label")}
          subtitle={t("ukv2.emotionalRhythm.subtitle")}
          shareText={`${data.morning}\n\n${data.night}`}
        >
          <AstriaUKV2FieldBlock label={t("ukv2.field.morning")} value={data.morning} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.midday")} value={data.midday} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.afternoon")} value={data.afternoon} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.evening")} value={data.evening} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.night")} value={data.night} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("ukv2.common.startNewReading")}
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Waves className="w-5 h-5" />} title={t("ukv2.tabs.emotionalRhythm.label")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("ukv2.tabs.emotionalRhythm.label")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("ukv2.emotionalRhythm.intro")}
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <UKV2TextInput value={dob} onChange={setDob} placeholder={t("ukv2.common.dobPlaceholder")} />
      </div>

      <UKV2SubmitButton onClick={onSubmit} disabled={!dob.trim()} label={t("ukv2.emotionalRhythm.submit")} />
    </>
  );
};

const EmotionalRhythmTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Give me my Emotional Rhythm for today. My Date of Birth: ${dob.trim()}.`);

  const data = message?.astriaUKV2Data as AstriaUKV2EmotionalRhythmData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="emotional-rhythm">
        {loading && <UKV2LoadingState label={t("ukv2.loading.emotionalRhythm")} />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="emotional-rhythm">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaUKV2Shell>
  );
};

export default EmotionalRhythmTab;
